import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Result, Row, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IUpdateBillableLookupListFormValues } from '../../../../../interfaces/lookup/billable';
import { IUpdateBillableOption } from '../../../../../interfaces/lookup/billable/data';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchBillableOptionById, updateBillableOption } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const BillableLookupListEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const itemId = params.id ? parseInt(params.id) : undefined;

    const onFinish = (values: IUpdateBillableLookupListFormValues) => {
        const body: IUpdateBillableOption = {
            id: lookupSlice.detailsPage.billableOption.data?.id,
            ...(values as any),
        };

        dispatch(updateBillableOption(body));
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.billableOption.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/billable', { replace: true });
        }
    }, [lookupSlice.detailsPage.billableOption.updateStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchBillableOptionById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    useEffect(() => {
        if (lookupSlice.detailsPage.billableOption.data) {
            form.setFieldsValue({
                id: lookupSlice.detailsPage.billableOption.data.id,
                code: lookupSlice.detailsPage.billableOption.data.code,
                description: lookupSlice.detailsPage.billableOption.data.description,
            } as IUpdateBillableLookupListFormValues);
        }
    }, [lookupSlice.detailsPage.billableOption.data]);

    if (lookupSlice.detailsPage.billableOption.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <Form
            form={form}
            name="billable-lookup-edit"
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={(changedValue: any, values) => {
                form.setFieldsValue(values);
            }}
        >
            <ContentHeader
                title="Manage billable option lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Billable option',
                        path: '/admin/lookup-lists/billable',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.billableOption.data?.description || '-',
                        path: `/admin/lookup-lists/billable/${params.id}/edit`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.billableOption.fetchStatus) && (
                    <Form.Item>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => navigate(-1)}
                                disabled={
                                    lookupSlice.detailsPage.billableOption.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.billableOption.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Close
                            </Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={
                                    lookupSlice.detailsPage.billableOption.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.billableOption.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Save and close
                            </Button>
                        </Space>
                    </Form.Item>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.billableOption.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.billableOption.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.billableOption.fetchStatus} about={'Billable'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Billable option Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Code</label>
                                    <Form.Item name="code" rules={[{ required: true, message: "'Code' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Description</label>
                                    <Form.Item name="description" rules={[{ required: true, message: "'Description' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Default</label>
                                    <p>{lookupSlice.detailsPage.billableOption?.data?.isDefault ? 'Yes' : 'No'}</p>
                                </Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.billableOption?.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.billableOption.created
                                            ? dayjs(lookupSlice.detailsPage.billableOption?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.billableOption.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.billableOption.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.billableOption.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </Form>
    );
};
